import { useEffect, useState } from "react";
import {
  adminGetBanners,
  adminCreateBanner,
  adminUpdateBanner,
  adminDeleteBanner,
} from "../../utils/api";

const EMPTY = { title: "", subtitle: "", order: 0, image: null };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    adminGetBanners()
      .then((r) => setBanners(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPreview(null);
    setError("");
    setModal(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ title: b.title || "", subtitle: b.subtitle || "", order: b.order || 0, image: null });
    setPreview(
      b.image ? (b.image.startsWith("http") ? b.image : `${b.image}`) : null
    );
    setError("");
    setModal(true);
  };

  const handleChange = (e) => {
    if (e.target.name === "image") {
      const file = e.target.files[0];
      setForm({ ...form, image: file });
      if (file) setPreview(URL.createObjectURL(file));
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && !form.image) {
      setError("Зображення обов'язкове для нового банера");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("subtitle", form.subtitle);
      fd.append("order", form.order);
      if (form.image) fd.append("image", form.image);

      if (editing) await adminUpdateBanner(editing.id, fd);
      else await adminCreateBanner(fd);

      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Помилка збереження");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цей банер?")) return;
    try {
      await adminDeleteBanner(id);
      load();
    } catch {
      alert("Помилка видалення");
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Банери</h1>
        <button className="admin-btn primary" onClick={openCreate}>
          + Додати банер
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Зображення</th>
                <th>Заголовок</th>
                <th>Підзаголовок</th>
                <th>Порядок</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {banners.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", padding: 40, color: "var(--text-light)" }}
                  >
                    Банерів немає
                  </td>
                </tr>
              ) : (
                banners.map((b) => {
                  const imgUrl = b.image
                    ? b.image.startsWith("http")
                      ? b.image
                      : `${b.image}`
                    : null;
                  return (
                    <tr key={b.id}>
                      <td>
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={b.title}
                            className="admin-thumb"
                            style={{ width: 100, height: 52, objectFit: "cover", borderRadius: 6 }}
                          />
                        ) : (
                          <div className="admin-placeholder">🖼️</div>
                        )}
                      </td>
                      <td>
                        <strong>{b.title || "—"}</strong>
                      </td>
                      <td style={{ color: "var(--text-light)" }}>{b.subtitle || "—"}</td>
                      <td>{b.order}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button className="admin-btn secondary" onClick={() => openEdit(b)}>
                            Редагувати
                          </button>
                          <button className="admin-btn danger" onClick={() => handleDelete(b.id)}>
                            Видалити
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div className="admin-modal">
            <h2>{editing ? "Редагувати банер" : "Новий банер"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Зображення {!editing && "*"}</label>
                <input name="image" type="file" accept="image/*" onChange={handleChange} />
                {preview && (
                  <img
                    src={preview}
                    alt="preview"
                    style={{
                      marginTop: 10,
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                )}
              </div>
              <div className="admin-form-group">
                <label>Заголовок</label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Великий текст на банері"
                />
              </div>
              <div className="admin-form-group">
                <label>Підзаголовок</label>
                <input
                  name="subtitle"
                  value={form.subtitle}
                  onChange={handleChange}
                  placeholder="Менший текст під заголовком"
                />
              </div>
              <div className="admin-form-group">
                <label>Порядок відображення</label>
                <input
                  name="order"
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>
              {error && (
                <p style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: 8 }}>{error}</p>
              )}
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setModal(false)}
                >
                  Скасувати
                </button>
                <button type="submit" className="admin-btn primary" disabled={submitting}>
                  {submitting ? "Збереження..." : editing ? "Зберегти" : "Створити"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
